import { Box, Typography } from "@mui/material";
import { useFinancialPreferences, useFinancialVisibility } from "shared/contexts/UserContext";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { formatCurrencyAmount, MASKED_BALANCE_PLACEHOLDER } from "shared/money/balances";
import type { CurrencyBalances } from "types/Balance";

export interface BalanceDisplayProps {
    /** Single amount when currency is set */
    amount?: number;
    /** Source currency for amount */
    currency?: string;
    /** Alternative: native amounts per currency */
    balancesByCurrency?: CurrencyBalances;
    /** Green vs red color */
    isPositive?: boolean;
    /** Typography variant */
    variant?: "body1" | "body2" | "h6" | "h5" | "h4" | "h3" | "h2" | "h1";
    fontSize?: string | number;
    /** Show currency code (e.g. USD) next to amount */
    showCurrencyCode?: boolean;
    /** Click handler */
    onClick?: () => void;
    /**
     * When true (and not using `balancesByCurrency`), show `amount` in `currency` without
     * converting to profile `displayCurrency`. Use for **market quote** totals (e.g. portafolio
     * IOL ARS/USD); leave false for debts, wallet, and other “unified display currency” views.
     */
    keepOriginalCurrency?: boolean;
    /** When set, historical bolsa rates are used for this conversion. */
    conversionDate?: string | Date;
}

const BalanceDisplay = ({
    amount = 0,
    currency = "USD",
    balancesByCurrency,
    isPositive = true,
    variant = "body2",
    fontSize,
    showCurrencyCode = true,
    onClick,
    keepOriginalCurrency = false,
    conversionDate,
}: BalanceDisplayProps) => {
    const { userSettings } = useFinancialPreferences();
    const { balancesVisible } = useFinancialVisibility();
    const { convertAmount, convertBalances } = useCurrencyConversion();
    const displayCurrency = userSettings.displayCurrency;

    const useOriginal =
        keepOriginalCurrency && balancesByCurrency == null;
    const amountInDisplayCurrency = useOriginal
        ? amount
        : balancesByCurrency != null && Object.keys(balancesByCurrency).length > 0
          ? convertBalances(balancesByCurrency, { date: conversionDate })
          : convertAmount(amount, currency, { date: conversionDate });
    const displayCurr = useOriginal ? currency : displayCurrency;

    const colorSx = {
        color: isPositive ? "success.main" : "error.main",
        fontWeight: 600,
        fontSize,
    };

    const boxSx = {
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { opacity: 0.7 } : {},
    } as const;

    if (!balancesVisible) {
        return (
            <Box sx={boxSx} onClick={onClick}>
                <Typography variant={variant} sx={colorSx} aria-label="Balances hidden">
                    {MASKED_BALANCE_PLACEHOLDER}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={boxSx} onClick={onClick}>
            <Typography variant={variant} sx={colorSx}>
                {formatCurrencyAmount(Math.abs(amountInDisplayCurrency), displayCurr)}
            </Typography>
            {showCurrencyCode && (
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                    }}
                >
                    {displayCurr}
                </Typography>
            )}
        </Box>
    );
};

export default BalanceDisplay;
